import React from 'react';

import {
  Box,
  VStack,
  Flex,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftAddon,
  Select,
  IconButton,
  Button,
  Collapse,
  SimpleGrid,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { BsFilter } from 'react-icons/bs';

import { useColors } from './colors';

const OrdersFilterSection = ({
  searchField,
  setSearchField,
  searchQuery,
  setSearchQuery,
  onSearch,
  filters,
  onFilterChange,
  showFilters,
  setShowFilters,
  storeOptions = [],
}) => {
  const {
    borderColor,
    textColor,
    mutedTextColor,
    inputColor,
    filterSectionBg,
    filterBg,
    filterOptionHover,
    filterOptionChecked,
    filterHover,
    filterChecked,
  } = useColors();

  const selectStyles = {
    option: { bg: filterBg, color: inputColor },
    '> option:hover': { bg: `${filterOptionHover}!important` },
    '> option:checked': { bg: `${filterOptionChecked}!important` },
  };

  const selectProps = {
    sx: selectStyles,
    _hover: { bg: filterHover },
    _focus: { bg: filterChecked, borderColor: 'blue.300' },
  };

  const clearFilters = () => {
    onFilterChange('clear', {
      marketplace: '',
      store: '',
      fulfillmentStatus: '',
      dateFrom: '',
      dateTo: '',
      lineItemCount: '',
    });
  };

  return (
    <VStack spacing={4} align="stretch">
      <Flex gap={4} flexWrap="wrap" align="center">
        <Box flex={2} minW="300px">
          <InputGroup width="100%" maxW="640px">
            <InputLeftAddon p={0} borderRightRadius={0} bg={filterSectionBg}>
              <Select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                h="100%"
                border="none"
                {...selectProps}
              >
                <option value="all">All fields</option>
                <option value="orderId">Order ID</option>
                <option value="buyer">Buyer</option>
                <option value="sku">SKU / ASIN</option>
              </Select>
            </InputLeftAddon>
            <Input
              color={inputColor}
              placeholder="Search orders…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onSearch()}
              borderLeftRadius={0}
            />
            <InputRightElement>
              <IconButton icon={<SearchIcon />} aria-label="Search" size="sm" onClick={onSearch} />
            </InputRightElement>
          </InputGroup>
        </Box>

        <Button
          leftIcon={<BsFilter />}
          variant={showFilters ? 'solid' : 'outline'}
          colorScheme={showFilters ? 'blue' : 'gray'}
          onClick={() => setShowFilters(!showFilters)}
        >
          Filters
        </Button>
      </Flex>

      <Collapse in={showFilters} animateOpacity>
        <Box
          p={4}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          bg={filterSectionBg}
        >
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="semibold" color={textColor} fontSize="sm">
              Order filters
            </Text>
            <Button size="xs" variant="ghost" onClick={clearFilters}>
              Clear all
            </Button>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="xs" color={mutedTextColor}>
                Marketplace
              </FormLabel>
              <Select
                size="sm"
                value={filters.marketplace}
                onChange={(e) => onFilterChange('marketplace', e.target.value)}
                {...selectProps}
              >
                <option value="">All</option>
                <option value="ebay">eBay</option>
                <option value="amazon">Amazon</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color={mutedTextColor}>
                Store
              </FormLabel>
              <Select
                size="sm"
                value={filters.store}
                onChange={(e) => onFilterChange('store', e.target.value)}
                {...selectProps}
              >
                <option value="">All stores</option>
                {storeOptions.map((s) => (
                  <option key={s._id || s.id} value={s._id || s.id}>
                    {s.name || s.storeName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color={mutedTextColor}>
                Fulfillment status
              </FormLabel>
              <Select
                size="sm"
                value={filters.fulfillmentStatus}
                onChange={(e) => onFilterChange('fulfillmentStatus', e.target.value)}
                {...selectProps}
              >
                <option value="">All</option>
                <option value="NOT_STARTED">Not started</option>
                <option value="IN_PROGRESS">In progress</option>
                <option value="FULFILLED">Fulfilled</option>
                <option value="CANCELLED">Canceled</option>
                <option value="Unshipped">Unshipped (Amazon)</option>
                <option value="Shipped">Shipped</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color={mutedTextColor}>
                Line items
              </FormLabel>
              <Input
                size="sm"
                type="number"
                min={1}
                placeholder="Any"
                color={inputColor}
                value={filters.lineItemCount}
                onChange={(e) => onFilterChange('lineItemCount', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onFilterChange('lineItemCount', e.target.value);
                }}
              />
              <Text fontSize="xs" color={mutedTextColor} mt={1}>
                Exact count — e.g. 3 shows only orders with 3 line items
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color={mutedTextColor}>
                Created from (system timezone)
              </FormLabel>
              <Input
                size="sm"
                type="date"
                color={inputColor}
                value={filters.dateFrom}
                onChange={(e) => onFilterChange('dateFrom', e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color={mutedTextColor}>
                Created to (system timezone)
              </FormLabel>
              <Input
                size="sm"
                type="date"
                color={inputColor}
                value={filters.dateTo}
                onChange={(e) => onFilterChange('dateTo', e.target.value)}
              />
            </FormControl>
          </SimpleGrid>
        </Box>
      </Collapse>
    </VStack>
  );
};

export default OrdersFilterSection;
